<template>
  <v-form ref="form" v-model="valid">
    <v-container>
      <v-layout row wrap>
        <v-flex xs6 pl-5>
          <template v-if="multiple && isCreate && !isCsv && !pastedUsers">
            <v-btn class="add-user-btn"
                  :style="{'margin-top': duplicateUsernames.length !== 0 ? '120px': '50px'}"
                  fab small fixed
                  color="primary"
                  @click="addUser">
              <v-icon>add</v-icon>
            </v-btn>
            <v-btn class="add-user-btn"
                  :style="{'margin-top': duplicateUsernames.length !== 0 ? '180px': '110px'}"
                  fab small fixed
                  color="primary"
                  @click="editPaste">
              <v-icon>edit</v-icon>
            </v-btn>
          </template>

          <v-csv-uploader v-if="isCreate"
                          class="d-inline-block"
                          :button-text="csv.filename === null ? 'Upload CSV File' : 'Re-upload CSV File'"
                          button-color="primary darken-2"
                          :fields="csv.fields"
                          @parsed="onParsedCsvFile"
                          :hide-per-row-menu="true"
                          data-test="createAccountsCsvUploader">
            <template v-slot:dialog-title="{ filename }">
              <p>Add credentials from <strong>{{ filename }}</strong> file</p>
            </template>
            <template v-slot:dialog-instructions="{ }">
              <p class="subheading text-black font-weight-regular">
                Assign your csv file <v-chip class="font-weight-bold" color="primary" text-color="white">columns</v-chip> to account form <v-chip class="font-weight-bold" color="indigo" text-color="white">fields</v-chip>.
              </p>
            </template>
          </v-csv-uploader>
          <template v-if="multiple && users.length !== 0">
            <v-toolbar transparent flat color="white" v-show="duplicateUsernames.length !== 0 && isCreate">
              <v-subheader>Some user IDs already exist</v-subheader>
            </v-toolbar>

            <v-expansion-panels v-if="!isCreate" multiple class="pr-5">
              <v-expansion-panel v-for="(selectedUser, userIndex) in users" :key="`user-${userIndex}`">
                <template>
                  <v-icon :color="noChangesForUser(selectedUser) ? 'primary' : 'error'">{{ noChangesForUser(selectedUser) ? $vuetify.icons.expand : 'error' }}</v-icon>
                </template>
                <v-expansion-panel-header>
                  <div>{{ selectedUser.username }}</div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <v-card-text class="grey lighten-3">
                      <p><strong><v-icon>public</v-icon> Location</strong> {{ selectedUser.assessment_location }}</p>
                      <p><strong><v-icon>group</v-icon> Assessment Group</strong> {{ selectedUser.assessment_group }}</p>
                      <p><strong><v-icon>timer</v-icon> Assessment Modifier</strong> {{ selectedUser.assessment_duration_modifier }}</p>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-data-table v-if="isCreate"
                          :items="users"
                          ref="usernames"
                          class="elevation-1 mx-4"
                          hide-default-headers hide-default-footer>
              <template v-slot:item="{ item, index }">
                <tr class="table-row"
                    :class="{ 'error': duplicateUsernames.includes(item.username) && isCreate,
                              'highlight': index === highlight }">
                  <td>
                    <v-icon style="float: right"
                            v-if="isCreate"
                            @click="remove(index)">
                      clear
                    </v-icon>
                    {{ item.username }}
                  </td>
                </tr>
              </template>
            </v-data-table>
            <template v-for="(error, errorIndex) in csv.errors">
              <small class="red--text" :key="`csv-error-${errorIndex}`">{{ error }}</small>
            </template>
            <div id="bottom"></div>
          </template>
          <template v-else-if="multiple">
            <v-layout class="ma-2">
              <h3>Please paste a list of users in the text box below, or drag a csv file into the page</h3>
            </v-layout>
            <v-layout>
              <v-flex class="text-xs-center">
                <v-textarea solo auto-grow
                            v-model="pastedUsers"
                            :error="pasteErrors.length !== 0"
                            @input="pasteErrors = []"
                            :error-messages="pasteErrors"
                            data-test="multipleAccountsInput"></v-textarea>
                <v-toolbar class="mr-0 pr-0 action-toolbar" flat color="white">
                  <v-spacer></v-spacer>
                  <v-btn :disabled="!pastedUsers || pastedUsers.length === 0 || pasteErrors.length !== 0"
                        @click="parseUsers(pastedUsers)"
                        color="success darken-2"
                        :loading="pasteLoading"
                        class="pl-1"
                        data-test="submitMultipleAccountsUsernames">
                    <v-icon class="ml-1" left>check</v-icon>
                    Done
                  </v-btn>
                </v-toolbar>
              </v-flex>
            </v-layout>
          </template>
          <template v-else>
            <v-btn color="primary"
                  @click="toggleMultiple"
                  class="pl-1 ml-0"
                  data-test="createMultipleAccounts">
              <v-icon class="ml-1" left>group</v-icon>
              Multiple Accounts
            </v-btn>

            <v-text-field
              readonly
              onfocus="this.removeAttribute('readonly')"
              v-model="user.username"
              :rules="rules.username"
              :error-messages="usernameErrors"
              label="Username" required
              data-test="username"></v-text-field>

          </template>
        </v-flex>
        <v-flex xs6>
          <v-select
            :items="options"
            v-model="user.assessment_location"
            :rules="disabledLocation ? [] : rules.location"
            @change="changeLocation"
            :hint="distinctUserAssessmentAttribute('assessment_location')"
            persistent-hint
            :disabled="disabledLocation"
            :label="locationLabel" required clearable
            data-test="location"></v-select>
          <v-text-field
            v-model="user['assessment_group']"
            :label="groupLabel"
            :hint="distinctUserAssessmentAttribute('assessment_group')"
            persistent-hint></v-text-field>

          <account-type-field :config.sync="user.assessment_config" v-model="user.user_type" v-if="isCreate" :userTypeOptions="userTypeOptions"/>

          <v-toolbar class="mt-3 mr-0 pr-0 action-toolbar" flat color="white">
            <v-spacer></v-spacer>
            <v-btn :disabled="noChanges"
                   v-if="!isCreate || isCsv"
                   color="secondary darken-2"
                   @click="reset"
                   class="pl-1 mr-2">
              <v-icon class="ml-1" left>refresh</v-icon>
              Reset
            </v-btn>
            <v-btn :disabled="(multiple && users.length === 0) || (duplicateUsernames.length !== 0 && isCreate) || (isCsv && csv.errors.length !== 0)"
                   color="success darken-2"
                   @click="next"
                   class="pl-1"
                   data-test="finishAccountsInfo">
              <v-icon class="ml-1" left>check</v-icon>
              Next
            </v-btn>
          </v-toolbar>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import { Bus } from '../../../bus'
import { parseFromText } from '../../../services/Accounts'
import Account from '../../../mixins/Account'
import FormStep from '../../../mixins/FormStep'
import { Rules } from '../../../helpers'
import CurrentSession from '../../../mixins/CurrentSession'
import VCsvUploader from '@/components/CsvUploader'

export default {
  name: 'AccountInfoStep',
  mixins: [Account, FormStep, CurrentSession],
  components: {
    VCsvUploader
  },
  created () {
    this.updateCachedCsvSelections()
    this.setLocations()
  },
  data () {
    return {
      multiple: false,
      pastedUsers: null,
      options: [],
      requiredFields: ['username'],
      users: [],
      pasteLoading: false,
      highlight: null,
      timeout: 0,
      pasteErrors: [],
      csv: {
        filename: null,
        headers: [],
        users: [],
        errors: [],
        fields: [
          {
            name: 'username',
            searchIn: ['username', 'usernames', 'user_id', 'user id', 'user_ids', 'user ids'],
            required: true
          },
          {
            name: 'location',
            searchIn: ['location', 'locations', 'country', 'countries', 'city', 'cities']
          },
          {
            name: 'group_id',
            searchIn: ['group_id', 'group_ids', 'group id', 'group ids', 'assessment_group', 'assessment_groups', 'assessment group', 'assessment groups']
          },
          {
            name: 'user_type',
            searchIn: ['user_type', 'user_types', 'user type', 'user types']
          }
        ]
      },
      previous: {
        csv: {
          headers: [],
          users: []
        },
        users: [],
        user: {}
      },
      disabledLocation: false
    }
  },
  beforeDestroy () {
    clearTimeout(this.timeout)
  },
  watch: {
    /**
     * Watch csv and remove it became empty
     */
    'csv.users': {
      immediate: true,
      deep: true,
      handler () {
        if (this.csv.users.length === 0) {
          this.$set(this.csv, 'filename', null)
          this.$set(this.csv, 'headers', [])
          this.$set(this.user, 'assessment_location', null)
          this.$set(this.user, 'assessment_group', null)
          this.$set(this.user, 'expires_at', null)
        }
      }
    }
  },
  computed: {
    usernames () {
      return this.multiple ? this.users.map(user => user.username) : [this.user.username]
    },
    /**
     * Check if there's any change that has been made
     */
    noChanges () {
      const noChangesForUser = JSON.stringify(this.user) === JSON.stringify(this.previous.user)

      if (this.isCsv) {
        const noChangesForUsers = JSON.stringify(this.users) === JSON.stringify(this.previous.users)
        return noChangesForUser && noChangesForUsers
      }

      return noChangesForUser
    },
    /**
     * Check if there is an accepted CSV file
     */
    isCsv () {
      return this.csv.users.length > 0
    },
    /**
     * User type options
     */
    userTypeOptions () {
      let options = []

      options.push({
        value: 'Candidate',
        label: 'Job Candidate'
      })
      options.push({
        value: 'Employee',
        label: 'Employee'
      })
      if (this.$can({ assessment: 'debug' })) {
        options.push({
          value: 'Demo',
          label: 'Imbellus User'
        })
      }
      options.push({
        value: 'trainer',
        label: 'A2E Facilitator'
      })
      return options
    },
    /**
     * Cached csv fields selection
     */
    cachedCsvSelections: {
      set (headers) {
        let caches = [...this.cachedCsvSelections]

        headers.forEach(header => {
          let cache = caches.find(cachedItem => cachedItem.name === header.field)
          let field = this.csv.fields.find(field => field.name === header.field)

          if (cache && cache.searchIn.indexOf(header.value) === -1) {
            cache.searchIn.unshift(header.value)
          } else if (field && field.searchIn.indexOf(header.value) === -1) {
            caches.push({ name: header.field, searchIn: [ header.value ] })
            field.searchIn.unshift(header.value)
          }
        })

        localStorage.setItem('accountsCsv', JSON.stringify(caches))
      },
      get () {
        return JSON.parse(localStorage.getItem('accountsCsv') || '[]')
      }
    },
    /**
     * Location field label
     */
    locationLabel () {
      return this.isUpdate
        ? 'Override Location'
        : 'Location'
    },
    /**
     * Assessment Group field label
     */
    groupLabel () {
      return this.isUpdate
        ? 'Override Assessment Group (optional)'
        : 'Assessment Group (optional)'
    }
  },
  methods: {
    /**
     * Transform the data passed to to an update form
     */
    entered () {
      if ('users' in this.data) {
        this.users = this.data.users
      }
      if ('user' in this.data) {
        Object.keys(this.data.user).forEach(key => {
          this.$set(this.user, key, this.data.user[key])
        })

        if ('auth_type' in this.data.user) {
          this.data['authentication'] = this.data.user['auth_type']
        }
      }
      if (!this.isCreate) {
        this.multiple = true
        // Create a copy of provided data to enable a reset function
        this.copy(this.user, this.previous.user)
      }
    },
    /**
     * Parse users pasted into the textarea
     *
     * @param text
     */
    parseUsers (text) {
      try {
        this.users = parseFromText(text, false, this.requiredFields, this.rules, '\n', '\t')
        this.pastedUsers = null
      } catch (err) {
        this.pasteErrors.push(err.toString())
      }
      this.pasteLoading = false
    },
    /**
     * Add a new user (when in multiple mode)
     */
    addUser () {
      const username = prompt('Please enter a new username', '')
      if (username !== null) {
        if (Rules.account.username.every(rule => rule(username) === true)) {
          this.users.push({ username })
          Bus.$emit('scroll-dialog')
          this.highlight = this.users.length - 1
          this.timeout = setTimeout(() => {
            this.highlight = null
          }, 3000)
        } else {
          const message = Rules.account.username.map(rule => rule(username)).filter(x => x !== true)[0]
          Bus.$emit('alert', { color: 'error', message })
        }
      }
    },
    /**
     * Show the textarea and edit multiple users
     */
    editPaste () {
      this.pastedUsers = this.users.map(user => user.username).join('\n')
      this.users = []
    },
    formData () {
      return { users: this.users, user: this.user, csv: this.csv }
    },
    toggleMultiple () {
      this.user.username = null
      this.multiple = true
      this.pastedUsers = []
    },
    changeLocation () {
      if (this.isCreate) {
        this.user.location = this.user.assessment_location
      }
    },
    /**
     * Reset changes
     */
    reset () {
      this.copy(this.previous.user, this.user)

      if (this.isCreate && this.isCsv) {
        this.copy(this.previous.users, this.users)
        this.copy(this.previous.csv.users, this.csv.users)
      }
    },
    /**
     * Copy method
     */
    copy (from, to) {
      Object.keys(from).forEach(key => {
        this.$set(to, key, from[key])
      })
    },
    /**
     * Check if the user data has been changed
     */
    noChangesForUser (user) {
      if (this.isCreate) return ''
      return (this.user.assessment_location === null || this.user.assessment_location === '' || this.user.assessment_location === user.assessment_location) &&
        (this.user.assessment_group === null || this.user.assessment_group === '' || this.user.assessment_group === user.assessment_group)
    },
    /**
     * Get a list of distinct locations
     */
    distinctUserAssessmentAttribute (attribute) {
      if (this.isCreate) return ''

      let data = [...new Set(this.users.map(user => user[attribute]))]
      data.sort()
      if (data.indexOf(null) > -1) {
        data[data.indexOf(null)] = '"EMPTY"'
      }

      return data.length > 1
        ? data.join(', ')
        : ''
    },
    /**
     * Get csv file data
     */
    onParsedCsvFile (csv) {
      this.csv.errors = []

      const usernames = csv.items.map(user => {
        return { username: user[csv.headers.find(header => header.field === 'username').value] }
      })

      try {
        this.users = parseFromText(usernames.map(user => user.username).join('\n'), false, this.requiredFields, this.rules, '\n', '\t')
      } catch (err) {
        this.csv.errors.push(err.toString())
      }

      // Validate headers
      const usernameHeader = csv.headers.find(header => header.field === 'username')
      const userTypeHeader = csv.headers.find(header => header.field === 'user_type')
      const locationHeader = csv.headers.find(header => header.field === 'location')

      let passedValidation = true

      csv.items.forEach(user => {
        if (Rules.account.username[1](user[usernameHeader.value]) !== true) {
          Bus.$emit('alert', { color: 'error', message: Rules.account.username[1](user[usernameHeader.value]) })
          passedValidation = false
        }
        if (userTypeHeader) {
          if (this.userTypeOptions.findIndex(option => option.value === user[userTypeHeader.value]) === -1) {
            Bus.$emit('alert', { color: 'error', message: 'Invalid user type' })
            passedValidation = false
          }
        }
        if (locationHeader) {
          if (!user[locationHeader.value]) {
            Bus.$emit('alert', { color: 'error', message: 'Invalid locations' })
            passedValidation = false
          }
        }
      })

      if (!passedValidation) {
        return
      }

      if (locationHeader) {
        this.disabledLocation = true
      }

      this.$set(this.csv, 'filename', csv.name)
      this.$set(this.csv, 'headers', csv.headers)
      this.$set(this.csv, 'users', csv.items)

      // Update Cached CSV Selections
      this.cachedCsvSelections = csv.headers

      this.$set(this, 'users', usernames)
      this.populateCsvField('location', 'assessment_location')
      this.populateCsvField('group_id', 'assessment_group')
      this.populateCsvField('expires_at', 'expires_at')
      this.populateCsvField('user_type', 'user_type', this.userTypeOptions.map(option => option.value))

      this.copy(usernames, this.previous.users)
      this.copy(this.user, this.previous.user)
      this.copy(csv.items, this.previous.csv.users)

      this.toggleMultiple()
    },
    /**
     * Populate form fields to a user object
     */
    populateCsvField (field, userProperty, only = []) {
      const header = this.csv.headers.find(header => header.field === field)
      // If a match exists, and the same for every user assign its value to the field
      if (header && this.csv.users.every(user => user[header.value] === this.csv.users[0][header.value])) {
        // If specific values exists check first and abort if another provided
        if (only.length > 0 && only.indexOf(this.csv.users[0][header.value]) === -1) {
          if (only.length === 1) {
            this.$set(this.user, userProperty, only[0])
          }
          return
        }
        this.$set(this.user, userProperty, this.csv.users[0][header.value])
        return true
      }
    },
    /**
     * Remove an account from the list
     */
    remove (userIndex) {
      this.users.splice(userIndex, 1)
      if (this.isCsv) {
        this.csv.users.splice(userIndex, 1)
      }
    },
    /**
     * Update Cached csv fields selections
     */
    updateCachedCsvSelections () {
      this.cachedCsvSelections.forEach(item => {
        let field = this.csv.fields.find(field => field.name === item.name)

        if (field) {
          item.searchIn.forEach(searchIn => {
            if (field.searchIn.indexOf(searchIn) === -1) {
              field.searchIn.unshift(searchIn)
            }
          })
        }
      })
    },
    setLocations () {
      let locations = JSON.parse(JSON.stringify(this.$store.getters['scores/locations']))
      for (let location in locations) {
        this.options.push({ 'value': locations[location], 'text': locations[location] })
      }
    }
  }
}
</script>

<style lang="sass">
.action-toolbar .v-toolbar__content
  padding-right: 0 !important

.add-user-btn
  margin-left: -32px

.table-row
  transition: background-color 1s

  &.highlight
    background-color: #FFFF8D !important
</style>
